import {
  Radio, Sparkles, CheckCircle2, UserCheck, Send, ArrowUpCircle, Database, Check, X, Loader2,
} from "lucide-react";
import { timeAgo } from "@/lib/constants";

export const ACTIONS = [
  { key: "simulate-signal", label: "Simulate Incoming Signal", icon: Radio },
  { key: "generate-nba", label: "Generate Next Best Action", icon: Sparkles },
  { key: "approve", label: "Approve Action", icon: CheckCircle2 },
  { key: "send-to-human", label: "Send to Human", icon: UserCheck },
  { key: "execute", label: "Execute Communication", icon: Send },
  { key: "escalate", label: "Escalate", icon: ArrowUpCircle },
  { key: "write-back", label: "Write Outcome to CRM", icon: Database },
];

export const APPROVAL_LABELS = {
  not_started: "Not started",
  not_required: "Auto-approved by governance",
  pending_human: "Pending human approval",
  approved: "Approved by human",
};

export const enabled = (key, c) => {
  const wf = c.workflow;
  switch (key) {
    case "simulate-signal": return !wf.crm_writeback;
    case "generate-nba": return !wf.crm_writeback;
    case "approve": return wf.approval === "pending_human";
    case "send-to-human": return !wf.crm_writeback && wf.approval !== "pending_human" && wf.approval !== "approved";
    case "execute": return !!wf.nba && (wf.approval === "approved" || wf.approval === "not_required") && wf.execution !== "executed";
    case "escalate": return !wf.crm_writeback && c.escalation !== "level2";
    case "write-back": return wf.execution === "executed" && !wf.crm_writeback;
    default: return false;
  }
};

export const disabledReason = (key, c) => {
  const wf = c.workflow;
  if (enabled(key, c)) return "";
  if (wf.crm_writeback) return "Workflow complete — outcome already written back to CRM";
  switch (key) {
    case "approve": return "Enabled only when an action is pending human approval";
    case "send-to-human": return "Already pending or approved by human review";
    case "execute":
      if (!wf.nba) return "Generate a next best action first";
      if (wf.approval === "pending_human") return "Requires human approval first";
      return "Communication already executed";
    case "escalate": return "Already at maximum escalation level (Level 2)";
    case "write-back": return wf.execution !== "executed" ? "Execute the communication first" : "Outcome already written back";
    default: return "Not available in the current workflow state";
  }
};

export const approvalTone = (approval) => {
  if (approval === "pending_human") return "text-amber-400";
  if (approval === "approved") return "text-emerald-400";
  return "text-zinc-400";
};

export const EventList = ({ events, empty }) => (
  <div className="space-y-3">
    {events.length === 0 && <div className="text-xs text-zinc-600">{empty}</div>}
    {events.map((e, i) => (
      <div key={`${e.ts}-${i}`} className="flex gap-3 text-xs">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-zinc-300 leading-relaxed">{e.text}</div>
          <div className="text-zinc-600 font-mono text-[10px] mt-0.5">{e.source} · {timeAgo(e.ts)}</div>
        </div>
      </div>
    ))}
  </div>
);

export const Panel = ({ title, children, testId }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid={testId}>
    <h3 className="text-sm font-semibold text-zinc-200 mb-4 pb-3 border-b border-zinc-800">{title}</h3>
    {children}
  </div>
);

export const CheckIcon = ({ status }) => {
  if (status === "passed") return <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />;
  if (status === "rejected") return <X className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />;
  return <Loader2 className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />;
};

export const kycTone = (status) => {
  if (status === "Verified") return "text-emerald-400";
  if (status === "Action Required") return "text-red-400";
  return "text-amber-400";
};
